import * as Highcharts from 'highcharts';
import { CategoryGroup, ReusableFunctions } from './reusable-functions';
import { Router } from '@angular/router';
import { Chart } from 'highcharts';
import { DecimalPipe } from '@angular/common';
import { UserAccessService } from '../services/user-access.service';
import { IncentivesService } from 'app/modules/incentives/services/incentives.service';
import { NavigationHelpService } from '../services/navigation-help.service';

export namespace LimeGearCharts {
    export interface ChartData {
        currentDate: Date;
        yCurrent: number;
        yPredict?: number;
        previousDate?: Date;
        yPreviousMonth: number;
    }

    export interface DonutChartSeriesData {
        name: string;
        y: number;
        color: string;
    }

    export interface ChartDataPoint {
        x?: number | string;
        y: number;
        color?: string;
        name?: string;
        properties?: any;
        category?: number | string;
    }

    export interface ChartDataSeparation {
        current: Array<ChartDataPoint>;
        line: Array<ChartDataPoint>;
        prediction: Array<ChartDataPoint>;
    }

    export class LimeGearChart {
        constructor(
            private router: Router,
            private reusableFunctions: ReusableFunctions,
            private decimalPipe?: DecimalPipe,
            private userAccessService?: UserAccessService,
            private incentivesService?: IncentivesService,
            private navigationHelpService?: NavigationHelpService
        ) {}

        public createKPIScatterPlot(
            title: string,
            xAxisText: string,
            yAxisText: string,
            seriesData: any[],
            seriesColor: string,
            dataLabelsEnabled: boolean,
            roleName,
            urlSegment: string = 'branch'
        ): Highcharts.Options {
            const _this = this;
            return {
                chart: {
                    spacingBottom: 0,
                    spacingLeft: 0,
                    marginRight: 0,
                    spacingRight: 0,
                    spacingTop: 0,
                    type: 'scatter',
                    zooming: {
                        type: 'xy',
                    },
                    height: 700,
                },
                title: {
                    text: title,
                    align: 'left',
                    useHTML: true,
                    x: 5,
                },
                credits: {
                    enabled: false,
                },
                series: [
                    {
                        data: seriesData,
                        turboThreshold: 0,
                        type: 'scatter',
                        color: seriesColor,
                        marker: {
                            symbol: 'circle',
                        },
                        point: {
                            events: {
                                click: function (point) {
                                    if (roleName && roleName == 'Employee') {
                                        _this.router.navigate(
                                            ['/performance/scorecards', urlSegment],
                                            {
                                                queryParams: {
                                                    id: this.value,
                                                },
                                            }
                                        );
                                    } else if (roleName && roleName !== 'Employee') {
                                        _this.router.navigate(
                                            ['/performance/scorecards', urlSegment],
                                            {
                                                queryParams: {
                                                    level: roleName,
                                                    id: this.value,
                                                },
                                            }
                                        );
                                    }
                                },
                            },
                        },
                    },
                ],
                exporting: {
                    enabled: false,
                },
                xAxis: [
                    {
                        labels: {
                            enabled: true,
                            style: {
                                fontSize: '1em',
                            },
                        },
                        title: {
                            text: xAxisText,
                            style: {
                                fontSize: '1em',
                            },
                        },
                    } as Highcharts.XAxisOptions,
                ],
                yAxis: [
                    {
                        title: {
                            text: yAxisText,
                            style: {
                                fontSize: '1em',
                            },
                        },
                        labels: {
                            enabled: true,
                            style: {
                                fontSize: '1em',
                            },
                        },
                    } as Highcharts.YAxisOptions,
                ],
                legend: {
                    enabled: false,
                },
                plotOptions: {
                    scatter: {
                        dataLabels: {
                            format: '{point.name}',
                            enabled: dataLabelsEnabled,
                        },
                        marker: {
                            radius: 5,
                            states: {
                                hover: {
                                    enabled: true,
                                    lineColor: 'rgb(100,100,100)',
                                    lineWidth: 0,
                                },
                            },
                        },
                        states: {
                            hover: {},
                        },
                    },
                    series: {
                        maxPointWidth: 52,
                        groupPadding: 0,
                        gridLineWidth: 0,
                        minorGridLineWidth: 0,
                        pointStart: 0,
                        dataGrouping: {
                            enabled: false,
                        },
                        cursor: 'pointer',
                    } as Highcharts.PlotSeriesOptions,
                },
                tooltip: {
                    style: {
                        color: '#223540',
                        fontFamily: 'Roboto',
                        fontSize: '12',
                        height: 12,
                        opacity: 1,
                    },
                    borderColor: 'white',
                    backgroundColor: 'white',
                    borderRadius: 7,
                    borderWidth: 1,
                    shared: true,
                    useHTML: true,
                    formatter: function () {
                        const tooltipTitle = this.point?.properties?.titlePart;
                        const isTitleLong = tooltipTitle?.length > 17 ? true : false;
                        return `<div>
                        <div class="kpi-insights-tooltip-title">
                            <div class="kpi-insights-tooltip-icon">•</div>
                            ${
                                isTitleLong
                                    ? `<div style="font-size: 12px; font-weight: 700; min-width: 140px !important">${tooltipTitle}</div>`
                                    : `<div style="font-size: 12px; font-weight: 700">${tooltipTitle}</div>`
                            }
                        </div>
                      <table>
                        <tr>
                          <td align='left' style='padding:0'>${
                              this.point?.properties?.unitsLabel ?? ''
                          }: </td>
                          <td align='right' style="text-align: right;">${
                              this.point?.properties?.units ?? ''
                          }</td>
                        </tr>
                        <tr>
                          <td align='left' style='padding:0'>% of Target: </td>
                          <td align='right' style="text-align: right;">${
                              this.y > 999 ? _this.decimalPipe.transform(this.y, '1.0-0') : this.y
                          }</td>
                        </tr>
                        <tr>
                          <td align='left' style ='padding:0' >Volume: </td>
                          <td align='right' style="text-align: right;">${_this.reusableFunctions.formatMoney(
                              +this.x
                          )}</td>
                        </tr>
                        <tr>
                        <td align='left' style ='padding:0' >Units: </td>
                        <td align='right' style="text-align: right;">${
                            this.point?.properties?.nbr
                        }</td>
                      </tr>
                       </table>
                    </div>`;
                    },
                } as Highcharts.TooltipOptions,
            };
        }

        public createKPISparkline(
            seriesData: any[],
            time: string,
            unit: string,
            seriesType: string,
            dataType: 'currency' | 'number' | string = 'number'
        ): Highcharts.Options {
            let _this = this;
            return {
                chart: {
                    backgroundColor: null,
                    borderWidth: 0,
                    type: 'area',
                    margin: [2, 4, 4, 4],
                    width: 180,
                    height: 50,
                    style: {
                        overflow: 'visible',
                    },
                },
                title: {
                    text: '',
                },
                credits: {
                    enabled: false,
                },
                xAxis: {
                    labels: {
                        enabled: false,
                    },
                    title: {
                        text: null,
                    },
                    startOnTick: false,
                    endOnTick: false,
                    tickPositions: [],
                },
                yAxis: {
                    endOnTick: false,
                    startOnTick: false,
                    labels: {
                        enabled: false,
                    },
                    title: {
                        text: null,
                    },
                    tickPositions: [0],
                },
                legend: {
                    enabled: false,
                },
                tooltip: {
                    useHTML: true,
                    backgroundColor: null,
                    borderWidth: 0,
                    shadow: false,
                    hideDelay: 0,
                    outside: true,
                    shared: true,
                    formatter: function () {
                        let yValue = this.y;
                        let value = '';
                        if (yValue && dataType === 'currency') {
                            value = _this.reusableFunctions.formatMoney(yValue);
                        } else if (yValue) {
                            value = yValue.toLocaleString();
                        }
                        return `<div>
                            <table class="tooltip">
                              <tr>
                                <td align='left' style='padding:0'>${time} ${+this.x + 1}: </td>
                                <td align='right'>${value}</td>
                              </tr>
                             </table>
                          </div>`;
                    },
                },
                exporting: {
                    enabled: false,
                },
                series: [
                    {
                        type: seriesType === 'area' ? 'area' : 'column',
                        data: seriesData,
                    },
                ],
                plotOptions: {
                    series: {
                        animation: false,
                        lineWidth: 1,
                        shadow: false,
                        states: {
                            hover: {
                                lineWidth: 1,
                            },
                        },
                        marker: {
                            radius: 4,
                            states: {
                                hover: {
                                    radius: 5,
                                },
                            },
                        },
                    },
                    column: {
                        negativeColor: '#910000',
                        borderColor: 'silver',
                    },
                },
            };
        }

        public createKPINewSparkline(
            seriesData: any[],
            time: string,
            unit: string,
            seriesType: string,
            dataType: 'currency' | 'number' | string = 'number',
            periodDates?: string[],
            seriesColor?: string
        ): Highcharts.Options {
            // 6/16/23 TODO: Merge KPISparkline functions when all sparklines are updated. Keeping old version alive for now since multiple charts use old function.
            let _this = this;
            return {
                chart: {
                    backgroundColor: null,
                    borderWidth: 0,
                    type: 'area',
                    margin: [2, 4, 4, 4],
                    width: 180,
                    height: 50,
                    style: {
                        overflow: 'visible',
                    },
                },
                title: {
                    text: '',
                },
                credits: {
                    enabled: false,
                },
                xAxis: {
                    labels: {
                        enabled: false,
                    },
                    title: {
                        text: null,
                    },
                    startOnTick: false,
                    endOnTick: false,
                    tickPositions: [],
                },
                yAxis: {
                    endOnTick: false,
                    startOnTick: false,
                    labels: {
                        enabled: false,
                    },
                    title: {
                        text: null,
                    },
                    tickPositions: [0],
                },
                legend: {
                    enabled: false,
                },
                tooltip: {
                    style: {
                        color: '#223540',
                        fontFamily: 'Roboto',
                        fontSize: '12',
                        height: 12,
                        zIndex: 1,
                    },
                    borderColor: 'white',
                    backgroundColor: 'white',
                    borderRadius: 7,
                    borderWidth: 1,
                    shared: true,
                    useHTML: true,
                    outside: true,
                    hideDelay: 0,
                    formatter: function () {
                        let yValue = this.y;
                        let value = '';
                        let label = 'Amount';

                        if ((yValue || yValue === 0) && dataType === 'currency') {
                            value = _this.reusableFunctions.formatMoney(yValue);
                        } else if (yValue || yValue === 0) {
                            value = yValue.toLocaleString();
                            label = 'Units';
                        }

                        const date =
                            time === 'Month' || time === ''
                                ? _this.formatPeriodData(+this.x, periodDates)
                                : _this.formatDayPeriods(periodDates[+this.x]);
                        const defaultColor = 'rgb(124, 181, 236)';
                        const color = seriesColor ? seriesColor : defaultColor;
                        return `<div class="fundedLoanDist-container">
                                    <div class="fundedLoanDist-tooltip-title">
                                        <div class="fundedLoanDist-tooltip-icon" style="color: ${color}">•</div>
                                        <div style="font-size: 12px; font-weight: 700; padding-right: 10px">${date}</div>
                                    </div>
                                    <table>
                                        <tr style='padding-bottom: 15px'>
                                            <td align='left' style='padding:0; padding-right: 15px'>${label}:</td>
                                            <td align='right' style="text-align: right;">${value}</td>
                                        </tr>
                                    </table>
                                </div>`;
                    },
                },
                exporting: {
                    enabled: false,
                },
                series: [
                    {
                        type: seriesType === 'area' ? 'area' : 'column',
                        data: seriesData,
                    },
                ],
                plotOptions: {
                    series: {
                        animation: false,
                        lineWidth: 1,
                        shadow: false,
                        states: {
                            hover: {
                                lineWidth: 1,
                            },
                        },
                        marker: {
                            radius: 4,
                            states: {
                                hover: {
                                    radius: 5,
                                },
                            },
                        },
                    },
                    column: {
                        negativeColor: '#910000',
                        borderColor: 'silver',
                    },
                },
            };
        }

        public createRevenueSparkline(
            seriesData: any[],
            periodData: any[],
            seriesType: string,
            dataType: 'currency' | 'number' | string = 'number'
        ): Highcharts.Options {
            let _this = this;
            return {
                chart: {
                    backgroundColor: null,
                    borderWidth: 0,
                    type: 'area',
                    margin: [2, 4, 4, 4],
                    width: 180,
                    height: 50,
                    style: {
                        overflow: 'visible',
                    },
                },
                title: {
                    text: '',
                },
                credits: {
                    enabled: false,
                },
                xAxis: {
                    labels: {
                        enabled: false,
                    },
                    title: {
                        text: null,
                    },
                    startOnTick: false,
                    endOnTick: false,
                    tickPositions: [],
                },
                yAxis: {
                    endOnTick: false,
                    startOnTick: false,
                    labels: {
                        enabled: false,
                    },
                    title: {
                        text: null,
                    },
                    tickPositions: [0],
                },
                legend: {
                    enabled: false,
                },
                tooltip: {
                    style: {
                        color: '#223540',
                        fontFamily: 'Roboto',
                        fontSize: '12',
                        height: 12,
                        zIndex: 1,
                    },
                    borderColor: 'white',
                    backgroundColor: 'white',
                    borderRadius: 7,
                    borderWidth: 1,
                    shared: true,
                    useHTML: true,
                    outside: true,
                    hideDelay: 0,
                    formatter: function () {
                        let yValue = this.y;
                        let value = '';
                        if ((yValue || yValue === 0) && dataType === 'currency') {
                            value = _this.reusableFunctions.formatMoney(yValue);
                        } else if (yValue || yValue === 0) {
                            value = yValue.toLocaleString();
                        }

                        return `<div class="revenue-trends-container">
                          <div class="revenue-trends-tooltip-title">
                              <div class="revenue-trends-tooltip-icon">•</div>
                              <div style="font-size: 12px; font-weight: 700; padding-right: 8px">${_this.formatPeriodData(
                                  +this.x,
                                  periodData
                              )}</div>
                          </div>
                        <table>
                          <tr style='padding-bottom: 15px'>
                            <td align='left' style='padding:0; padding-right: 15px'>Amount:</td>
                            <td align='right' style="text-align: right;">${value}</td>
                          </tr>
                         </table>
                      </div>`;
                    },
                },
                exporting: {
                    enabled: false,
                },
                series: [
                    {
                        type: seriesType === 'area' ? 'area' : 'column',
                        data: seriesData,
                        lineColor: 'rgb(0,138,119)',
                    },
                ],
                plotOptions: {
                    series: {
                        animation: false,
                        lineWidth: 1,
                        shadow: false,
                        color: 'rgb(65,214,195)',
                        states: {
                            hover: {
                                lineWidth: 1,
                            },
                        },
                        marker: {
                            fillColor: 'rgb(0,138,119)',
                            radius: 4,
                            states: {
                                hover: {
                                    radius: 5,
                                },
                            },
                        },
                    },
                    column: {
                        negativeColor: '#910000',
                        borderColor: 'silver',
                    },
                },
            };
        }

        //if it passes in colarAxis, use it. Otherwise, use default gradient
        public setColorAxis(colorAxis): any {
            if (colorAxis != '') {
                return colorAxis;
            } else {
                return {
                    min: 0,
                    minColor: '#E6E7E8',
                    maxColor: '#005645',
                };
            }
        }

        public createNewAAGStackedChart(
            xAxisText: string,
            yAxisText: string,
            initialSeriesData: any,
            currentPeriodDates: string[],
            comparePeriodDates: string[],
            isBPS: boolean,
            route: string,
            urlSegment: string,
            compareTo: string,
            lineSeriesTitle: string,
            purpose: string,
            loanType: string,
            hFilterJson: string
        ): Highcharts.Options {
            const _this = this;
            let clickablePoints = this.userAccessService.hasAccessToPage(route + '/' + urlSegment);
            const categoryGroup: string[] = [];
            currentPeriodDates.forEach((date, i) => {
                categoryGroup.push(
                    this.reusableFunctions.getShortDateFormat(
                        _this.formatPeriodData(i, currentPeriodDates)
                    )
                );
            });
            return {
                chart: {
                    spacingBottom: 0,
                    spacingLeft: 0,
                    marginRight: 0,
                    spacingRight: 0,
                    spacingTop: 5,
                    height: 330,
                    events: {
                        load: function () {
                            if (clickablePoints) {
                                var xAxis = this.xAxis[0];
                                var xTicks = xAxis.ticks;
                                var yAxis = this.yAxis[0];
                                var points = this.series[0].points;
                                points.forEach((pointInf, i) => {
                                    if (xTicks[i]) {
                                        var label = xTicks[i].label.element;
                                        label.style.cursor = 'pointer';
                                    }
                                });
                            }
                        },
                    },
                },
                title: {
                    text: '',
                },
                credits: {
                    enabled: false,
                },
                series: initialSeriesData,
                exporting: {
                    enabled: false,
                    buttons: {
                        contextButton: {
                            //menuItems: Highcharts.defaultOptions.exporting.buttons.contextButton.menuItems.slice(0, 10)
                        },
                    },
                },
                xAxis: [
                    {
                        type: 'category',
                        categories: categoryGroup,
                        tickInterval: 1,
                        startOnTick: false,
                        pointIntervalUnit: xAxisText,
                        labels: {
                            enabled: true,
                            style: {
                                fontSize: '10px',
                                color: '#666666',
                            },
                        },
                    } as Highcharts.XAxisOptions,
                ],
                yAxis: [
                    {
                        title: {
                            text: yAxisText,
                            style: {
                                fontSize: '10px',
                                color: '#666666',
                            },
                        },

                        labels: {
                            enabled: true,
                            style: {
                                fontSize: '10px',
                                color: '#666666',
                            },
                        },
                        lineWidth: 1,
                    } as Highcharts.YAxisOptions,
                ],
                legend: {
                    enabled: true,
                    align: 'center',
                    verticalAlign: 'bottom',
                },
                plotOptions: {
                    column: {
                        stacking: 'normal',
                        minPointLength: 3,
                        states: {
                            hover: {
                                brightness: -0.3,
                            },
                        },
                    },
                    series: {
                        groupPadding: 0,
                        lineWidth: 3,
                        gridLineWidth: 0,
                        minorGridLineWidth: 0,
                        dataGrouping: {
                            enabled: false,
                        },
                        maxPointWidth: 50,
                        marker: {
                            enabled: true,
                            states: {
                                hover: {
                                    radius: 4,
                                    enabled: true,
                                    radiusPlus: 0,
                                    lineWidthPlus: 0,
                                    lineWidth: 0,
                                },
                            },
                        },
                        events: {
                            click: function (e) {
                                const isPriorPeriod = e.point.series.name === 'Prior Period';
                                if (isPriorPeriod) {
                                    const amount =
                                        e.point.properties.type === 'amount'
                                            ? e.point.y
                                            : e.point.properties.alternateY;
                                    const bps =
                                        e.point.properties.type === 'amount'
                                            ? e.point.properties.alternateY.toFixed(3)
                                            : e.point.y.toFixed(3);
                                    _this.incentivesService.openAAGModal(
                                        e.point.properties.yearMonthCalendar,
                                        this.xAxis.series[0].name.replace('Loan', 'Total'),
                                        amount,
                                        bps,
                                        isPriorPeriod,
                                        hFilterJson
                                    );
                                }
                            },
                        },
                        point: {
                            events: {
                                click: function (e) {
                                    const amount =
                                        this.properties.units === 'amount'
                                            ? this.y
                                            : this.properties.alternateY;
                                    const bps =
                                        this.properties.units === 'amount'
                                            ? this.properties.alternateY.toFixed(3)
                                            : this.y.toFixed(3);
                                    _this.incentivesService.openAAGModal(
                                        this.properties.datePeriodDescription,
                                        this.series.name,
                                        amount,
                                        bps,
                                        false,
                                        hFilterJson
                                    );
                                },
                            },
                        },
                        cursor: 'pointer',
                    } as Highcharts.PlotSeriesOptions,
                },
                tooltip: {
                    style: {
                        fontFamily: 'Roboto',
                        fontSize: '12',
                        height: 12,
                    },
                    borderColor: 'white',
                    backgroundColor: 'white',
                    borderRadius: 7,
                    borderWidth: 1,
                    shared: true,
                    useHTML: true,
                    outside: false,
                    formatter: function () {
                        let tooltip = '';
                        let length = this.points.length;
                        let previousPeriodLine = '';
                        let tooltipTotalName = '';
                        let propertyTotal: string | number =
                            this.points[0].point.y + this.points[1].point.y;
                        propertyTotal = isBPS
                            ? propertyTotal.toFixed(3) + ' BPS'
                            : _this.reusableFunctions.formatMoney(propertyTotal, 0, true);
                        let alternatePropertyTotal =
                            this.points[0].point.properties.alternateY +
                            this.points[1].point.properties.alternateY;
                        alternatePropertyTotal = isBPS
                            ? _this.reusableFunctions.formatMoney(alternatePropertyTotal, 0, true)
                            : alternatePropertyTotal.toFixed(3) + ' BPS';
                        tooltip =
                            '<table style="border-collapse: collapse">' +
                            this.points.reduce(function (s, point, index) {
                                let toolText = '';
                                let value = isBPS
                                    ? `${point.y.toFixed(3) + ' BPS'}`
                                    : _this.reusableFunctions.formatMoney(point.y, 0, true);
                                let seriesName = point.series.name;
                                let color = `color: ${point.color}`;
                                let isPreviousPeriod =
                                    seriesName.replace('Prior', 'Previous').toLowerCase() ===
                                    lineSeriesTitle.toLowerCase();
                                const alternateYValue = isBPS
                                    ? _this.reusableFunctions.formatMoney(
                                          point.point.properties.alternateY,
                                          0,
                                          true
                                      )
                                    : point.point.properties.alternateY.toFixed(3) + ' BPS';
                                const abbrevMonthDate = isPreviousPeriod
                                    ? _this.reusableFunctions.getShortDateFormat(
                                          _this.formatPeriodData(
                                              point.point.index,
                                              comparePeriodDates
                                          )
                                      )
                                    : null;
                                if (index === 0) {
                                    s += `<div class="financials-aag-container">
                                               <div class="financials-aag-tooltip-title">
                                                   <div class="financials-aag-tooltip-title-sub">${_this.formatPeriodData(
                                                       point.point.index,
                                                       currentPeriodDates
                                                   )}</div>
                                              </div>`;
                                    tooltipTotalName = point.point.properties.tootipTotalName;
                                }
                                if (!isPreviousPeriod) {
                                    toolText =
                                        s +
                                        `<tr>
                                            <td style="padding-right: 15px; display: flex; font-weight: 500;">
                                                <div style="display: flex; justify-content: space-between" class="tooltip-aag-title">
                                                    <div class="tooltip-aag-icon" style='${color}'>•</div>
                                                    <div style="font-size: 12px; margin-right: 50px">${seriesName}:</div>
                                                </div>
                                            </td>
                                            <td style="text-align: right; font-weight: 400">
                                                <span style="display: inline-block; width: 80px; margin-bottom: 6px">${value}</span>
                                                <span> | </span>
                                                <span style="display: inline-block; width: 73px">${alternateYValue}</span>
                                            </td>
                                        </tr>`;
                                }
                                if (isPreviousPeriod) {
                                    toolText = s;
                                    previousPeriodLine = `<tr>
                                            <td style="padding-right: 15px; display: flex; font-weight: 500" class="tooltip-aag-title">
                                                <div class="tooltip-aag-icon" style='${color}'>•</div>
                                                <div>Compared to ${abbrevMonthDate}:</div>
                                            </td>
                                            <td style="text-align: right; font-weight: 400">
                                                <span style="display: inline-block; width: 80px; margin-bottom: 6px">${value}</span>
                                                <span> | </span>
                                                <span style="display: inline-block; width: 73px">${alternateYValue}</span>
                                            </td>
                                        </tr>`;
                                }
                                if (index === length - 1) {
                                    toolText =
                                        s +
                                        `<tr style="border-top: 1px solid #505D6F; line-height: 16px">
                                            <td style="padding-right: 15px; display: flex; font-weight: 500" class="tooltip-aag-title">
                                                <div style="margin-left: 28px">Total ${tooltipTotalName}:</div>
                                            </td>
                                            <td style="text-align: right; font-weight: 400">
                                                <span style="display: inline-block; width: 80px; margin-bottom: 6px">${propertyTotal}</span> 
                                                <span> | </span>
                                                <span style="display: inline-block; width: 73px">${alternatePropertyTotal}</span>
                                            </td>
                                        </tr>` +
                                        previousPeriodLine;
                                }
                                return toolText;
                            }, '') +
                            '</table>';
                        return tooltip;
                    },
                } as Highcharts.TooltipOptions,
                lang: {
                    noData: 'There are no data results.',
                },
                noData: {
                    style: {
                        fontWeight: 'bold',
                        fontSize: '15px',
                        color: '#303030',
                    },
                },
            };
        }

        public createECloseAdoptionTrendsStackedChart(
            xAxisText: string,
            yAxisText: string,
            initialSeriesData: any,
            currentPeriodDates: string[],
            route: string,
            urlSegment: string,
            title: string
        ): Highcharts.Options {
            const _this = this;
            let clickablePoints = this.userAccessService.hasAccessToPage(route + '/' + urlSegment);
            const categoryGroup: string[] = [];
            currentPeriodDates.forEach((date) =>
                categoryGroup.push(_this.formatPeriodDataFromAbbrvMonth(date))
            );

            return {
                chart: {
                    spacingBottom: 0,
                    spacingLeft: 0,
                    marginRight: 0,
                    spacingRight: 0,
                    spacingTop: 5,
                    height: 570,
                    events: {
                        load: function () {
                            if (clickablePoints) {
                                var xAxis = this.xAxis[0];
                                var xTicks = xAxis.ticks;
                                var yAxis = this.yAxis[0];
                                var points = this.series[0].points;
                                points.forEach((pointInf, i) => {
                                    if (xTicks[i]) {
                                        var label = xTicks[i].label.element;
                                        label.style.cursor = 'pointer';
                                        label.onclick = function (item) {
                                            let queryParams = {
                                                selectedPeriod: pointInf.properties.period,
                                                titleSuffix:
                                                    title +
                                                    ': ' +
                                                    pointInf.properties.datePeriodDescription,
                                            };

                                            _this.navigationHelpService.navigateTo(
                                                ['/reporting/production/loans-funded-by'],
                                                queryParams,
                                                true
                                            );
                                        };
                                    }
                                });
                            }
                        },
                    },
                },
                title: {
                    text: '',
                    y: 40,
                    x: 10,
                },
                credits: {
                    enabled: false,
                },
                series: initialSeriesData,
                exporting: {
                    enabled: false,
                    buttons: {
                        contextButton: {
                            //menuItems: Highcharts.defaultOptions.exporting.buttons.contextButton.menuItems.slice(0, 10)
                        },
                    },
                },
                xAxis: [
                    {
                        type: 'category',
                        categories: categoryGroup,
                        tickInterval: 1,
                        startOnTick: false,
                        pointIntervalUnit: xAxisText,
                        labels: {
                            enabled: true,
                            style: {
                                fontSize: '10px',
                                color: '#666666',
                                align: 'top',
                            },
                        },
                    } as Highcharts.XAxisOptions,
                ],
                yAxis: [
                    {
                        title: {
                            text: yAxisText,
                            style: {
                                fontSize: '10px',
                                color: '#666666',
                            },
                        },

                        labels: {
                            enabled: true,
                            style: {
                                fontSize: '10px',
                                color: '#666666',
                            },
                        },
                        lineWidth: 1,
                    } as Highcharts.YAxisOptions,
                ],
                legend: {
                    enabled: true,
                    align: 'left',
                    verticalAlign: 'top',
                    itemStyle: {
                        fontSize: '14px',
                        fontWeight: 'normal',
                    },
                    margin: 30,
                    y: -10,
                    symbolHeight: 12,
                },
                plotOptions: {
                    column: {
                        stacking: 'normal',
                        minPointLength: 3,
                        states: {
                            hover: {
                                brightness: -0.3,
                            },
                        },
                    },
                    series: {
                        groupPadding: 0,
                        lineWidth: 3,
                        gridLineWidth: 0,
                        minorGridLineWidth: 0,
                        dataGrouping: {
                            enabled: false,
                        },
                        maxPointWidth: 50,
                        marker: {
                            enabled: true,
                            states: {
                                hover: {
                                    radius: 4,
                                    enabled: true,
                                    radiusPlus: 0,
                                    lineWidthPlus: 0,
                                    lineWidth: 0,
                                },
                            },
                        },
                        events: {
                            click: function (e) {},
                        },
                        point: {
                            events: {
                                click: function (e) {
                                    const date = this.properties.datePeriodDescription.includes('/')
                                        ? this.properties.datePeriodDescription
                                        : _this.formatPeriodDataFromAbbrvMonth(
                                              this.properties.datePeriodDescription
                                          );
                                    if (clickablePoints) {
                                        let queryParams = {
                                            selectedPeriod: this.properties.period,
                                            titleSuffix: `${title}: ${this.series.name} ${date}`,
                                            eCloseOptionKey: this.properties.eCloseOptionKey,
                                        };

                                        _this.navigationHelpService.navigateTo(
                                            ['reporting/production/loans-funded-by'],
                                            queryParams,
                                            true
                                        );
                                    }
                                },
                            },
                        },
                        cursor: 'pointer',
                    } as Highcharts.PlotSeriesOptions,
                },
                tooltip: {
                    style: {
                        fontFamily: 'Roboto',
                        fontSize: '12',
                        height: 12,
                    },
                    borderColor: 'white',
                    backgroundColor: 'white',
                    borderRadius: 7,
                    borderWidth: 1,
                    shared: true,
                    useHTML: true,
                    outside: false,
                    formatter: function () {
                        let tooltip = '';
                        let length = this.points.length;
                        let previousPeriodLine = '';
                        let tooltipTotalName = '';
                        let propertyTotal = `${this.points.reduce((a, { y }) => a + y, 0)} units`;
                        const unitsWidth = propertyTotal.length > 9 ? '65px' : '50px';
                        const volumeWidth = propertyTotal.length > 9 ? '85px' : '75px';

                        let alternatePropertyTotal = this.points.reduce(
                            (a, b) => a + b.point.properties.alternateY,
                            0
                        );
                        alternatePropertyTotal = _this.reusableFunctions.formatMoney(
                            alternatePropertyTotal,
                            0,
                            true
                        );

                        tooltip =
                            '<table style="border-collapse: collapse">' +
                            this.points.reduce(function (s, point, index) {
                                let toolText = '';
                                let value = point.y === 0 ? '-' : `${point.y} units`;
                                let seriesName = point.series.name;
                                let color = `color: ${point.color}`;
                                const alternateYValue =
                                    point.point.properties.alternateY === 0
                                        ? '-'
                                        : _this.reusableFunctions.formatMoney(
                                              point.point.properties.alternateY,
                                              0,
                                              true
                                          );

                                const tooltipTitle =
                                    point.point.properties.datePeriodDescription.includes('/')
                                        ? point.point.properties.datePeriodDescription
                                        : _this.reusableFunctions.getFullDateFormat(
                                              point.point.properties.datePeriodDescription
                                          );
                                // Tooltip title
                                if (index === 0) {
                                    s += `<div class="eclose-method-trends-container">
                                               <div class="eclose-method-trends-title">
                                                   <div class="eclose-method-trends-sub">${tooltipTitle}</div>
                                              </div>`;
                                    tooltipTotalName = 'Total Funded';
                                }
                                if (true) {
                                    toolText = s += `<tr>
                                            <td style="padding-right: 15px; display: flex; font-weight: 500;">
                                                <div style="display: flex; justify-content: space-between" class="tooltip-aag-title">
                                                    <div class="tooltip-aag-icon" style='${color}'>•</div>
                                                    <div style="font-size: 12px; margin-right: 50px">${seriesName}:</div>
                                                </div>
                                            </td>
                                            <td style="text-align: center; font-weight: 400">
                                                <span style="display: inline-block; width: ${unitsWidth}; margin-bottom: 6px">${value}</span>
                                                <span> | </span>
                                                <span style="display: inline-block; width: ${volumeWidth}">${alternateYValue}</span>
                                            </td>
                                        </tr>`;
                                }

                                // Totals
                                if (index === length - 1) {
                                    toolText =
                                        s +
                                        `<tr style="border-top: 1px solid #505D6F; line-height: 16px">
                                            <td style="padding-right: 15px; display: flex; font-weight: 500" class="tooltip-aag-title">
                                                <div style="margin-left: 28px">${tooltipTotalName}:</div>
                                            </td>
                                            <td style="text-align: center; font-weight: 400">
                                                <span style="display: inline-block; width: ${unitsWidth}; margin-bottom: 6px">${propertyTotal}</span> 
                                                <span> | </span>
                                                <span style="display: inline-block; width: ${volumeWidth}">${alternatePropertyTotal}</span>
                                            </td>
                                        </tr>` +
                                        previousPeriodLine;
                                }
                                return toolText;
                            }, '') +
                            '</table>';
                        return tooltip;
                    },
                } as Highcharts.TooltipOptions,
                lang: {
                    noData: 'There are no data results.',
                },
                noData: {
                    style: {
                        fontWeight: 'bold',
                        fontSize: '15px',
                        color: '#303030',
                    },
                },
            };
        }

        // Expected Period Format = ['YYYY-MM']
        formatPeriodData(index: number, periodData: string[]) {
            if (!periodData.length) return '';
            const months = [
                'January',
                'February',
                'March',
                'April',
                'May',
                'June',
                'July',
                'August',
                'September',
                'October',
                'November',
                'December',
            ];
            const dateArr = periodData[index].split('-');
            const month = months[Number(dateArr[1]) - 1];
            const year = dateArr[0];
            return `${month} ${year}`;
        }

        // Expected Period Input = ['YYYY-MM-DD'], Output = ['MM/DD/YYYY']
        formatDayPeriods(date: string) {
            const dateArr = date.split('-');
            return `${dateArr[1]}/${dateArr[2]}/${dateArr[0]}`;
        }

        formatPeriodDataFromAbbrvMonth(date) {
            if (date.includes('/')) return date;
            const dateArr = date.split('-');
            return `${dateArr[0]} '${dateArr[1].slice(-2)}`;
        }

        chartTitleString(
            title: string,
            comparisonValue: number,
            ComparisonPercent: number,
            isComparable: boolean,
            estimatedValue: number,
            mainValue: number,
            isEstimateVisisble: string = 'none'
        ): string {
            let plot_title: string = `
            <div class="title-content">
              <div class="dashboard-plot-inline-text-left">
                <label>ChartTile</label>
              </div>
              <div class="dashboard-plot-inline-text-right" style="color: {{ label_color }}">
                  <i class='caret_icon'></i>&ensp;PlotComparisonValue
              </div>
            </div>
            <div style="text-align: center;">
              <label class="dashboard-plot-main-value">PlotMainValue</label><br />
              <label class="dashboard-plot-toggle-value" style="display: {{ isEstimateVisisble }};">
                  Estimated: PlotEstimatedValue
              </label>
            </div>
            `;
            let comparison = this.reusableFunctions.getCaret(
                comparisonValue,
                false,
                ComparisonPercent,
                isComparable
            );

            if (estimatedValue === null) {
                isEstimateVisisble = 'none';
            }

            let replaceString: string = plot_title
                .replace('ChartTile', title)
                .replace('PlotComparisonValue', comparison.comparison)
                .replace('PlotEstimatedValue', this.reusableFunctions.formatMoney(estimatedValue))
                .replace('PlotMainValue', this.reusableFunctions.formatMoney(mainValue))
                .replace('caret_icon', comparison.caret_icon)
                .replace('label_color', comparison.color)
                .replace('isEstimateVisisble', isEstimateVisisble);
            return replaceString;
        }

        getPurpose(
            urlSegment: string,
            name: string,
            purpose: string,
            lineSeriesTitle: string
        ): any {
            if (urlSegment.includes('purpose')) {
                if (name.toLowerCase() === lineSeriesTitle.toLowerCase()) {
                    return purpose;
                }
                return name;
            } else {
                return purpose;
            }
        }

        getLoanType(
            urlSegment: string,
            name: string,
            loanType: string,
            lineSeriesTitle: string
        ): any {
            if (urlSegment.includes('loan-type') || urlSegment.includes('sold-to-investor')) {
                if (name.toLowerCase() === lineSeriesTitle.toLowerCase()) {
                    return loanType;
                }
                return name;
            } else {
                return loanType;
            }
        }

        setLineChartSeriesOptions(
            data,
            type: string,
            color: string,
            markerSymbol: string,
            clickablePoints: boolean = false,
            compareTo: string,
            route: string,
            urlSegment,
            name: string,
            purpose: string,
            loanType: string
        ) {
            const _this = this;
            return {
                data: data,
                type: type,
                color: color,
                marker: {
                    symbol: markerSymbol,
                },
                name: name,
                legendIndex: 20,
                point: {
                    events: {
                        click: function () {
                            if (clickablePoints) {
                                let queryParams = _this.getQueryParams(
                                    urlSegment,
                                    this.series.name,
                                    this.properties.period,
                                    compareTo,
                                    'C',
                                    purpose,
                                    loanType,
                                    this.series.name
                                );

                                _this.router.navigate([route, urlSegment], {
                                    queryParams: queryParams,
                                });
                            }
                        },
                    },
                },
            } as Highcharts.SeriesOptionsType;
        }

        public getQueryParams(
            urlSegment: string,
            seriesName,
            period: string,
            compareTo: string,
            periodGroup: string,
            purpose,
            loanType,
            lineSeriesTitle: string
        ) {
            let queryParams: Object;
            if (urlSegment.search('fall-out') > -1) {
                queryParams = {
                    selectedPeriod: period,
                    periodGroup: periodGroup,
                    falloutType:
                        seriesName.toLowerCase() === lineSeriesTitle.toLowerCase()
                            ? 'All'
                            : seriesName,
                    compareTo: compareTo,
                    titleSuffix: seriesName === 'All' ? '' : ': ' + seriesName,
                };
            } else {
                queryParams = {
                    purpose: this.getPurpose(urlSegment, seriesName, purpose, lineSeriesTitle),
                    loanType: this.getLoanType(urlSegment, seriesName, loanType, lineSeriesTitle),
                    compareTo: compareTo,
                    selectedPeriod: period,
                    periodGroup: periodGroup,
                    titleSuffix: seriesName === 'All' ? '' : ': ' + seriesName,
                };
            }
            return queryParams;
        }
    }
}
